import type { FlashMessage } from '@corp/components/FlashMessage.vue'
export type { FlashMessage }

let flashMessageIds = 0

// Flash messages that are currently being displayed.
let messages = $shallowRef<FlashMessage[]>([])
export const messagesRef = $$(messages)

// Persists shown flash messages so feature tests can assert on them even after
// the auto-dismiss timer removes them from the DOM. Capped to avoid unbounded growth.
const MESSAGE_HISTORY_LIMIT = 50
let messageHistory = $shallowRef<string[]>([])
export const messageHistoryRef = $$(messageHistory)

// Adds a message to the current page.
//
// NOTE: It's not possible to open several messages with the same id.
// An unique id is auto-generated unless one is provided.
export function addMessage ({ id = `message-${flashMessageIds++}`, duration, ...attrs }: FlashMessage) {
  if (!messages.some(message => message.id === id)) {
    duration ||= attrs.type === 'alert' ? 7000 : 5000
    messages = [...messages, { id, duration, ...attrs }]
    const nextHistory = [...messageHistory, attrs.message]
    messageHistory = nextHistory.length > MESSAGE_HISTORY_LIMIT
      ? nextHistory.slice(-MESSAGE_HISTORY_LIMIT)
      : nextHistory
  }
  return id
}

// Removes a message from the current page.
export function removeMessage (id: string) {
  messages = messages.filter(message => message.id !== id)
}

// Closes all messages.
export function closeAllMessages () {
  messages = []
}

// Clears the persisted message history. Mainly useful for tests.
export function clearMessageHistory () {
  messageHistory = []
}

// Stack of teleport targets for flash messages inside dialogs.
// When a dialog opens, it pushes its flash container; the topmost one receives notifications.
export interface FlashTarget {
  el: HTMLElement
  inline?: boolean
}

let flashTargets = $shallowRef<FlashTarget[]>([])
const activeFlashTarget = $computed(() => flashTargets.length > 0 ? flashTargets[flashTargets.length - 1] : null)
export const activeFlashTargetRef = $$(activeFlashTarget)

export function pushFlashTarget (target: FlashTarget) {
  flashTargets = [...flashTargets, target]
}

export function removeFlashTarget (el: HTMLElement) {
  flashTargets = flashTargets.filter(t => t.el !== el)
}
